import React, { useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components';
import { Text } from '@amzn/storm-ui';
import { RootState } from 'src/app/store';
import { rocket } from '@amzn/storm-ui-icons';
import {
  selectFitTotalFeatures,
  selectFitUnreadFeatures,
} from '../../store/slice';
import { PAGE_LAYOUT_TYPE } from 'src/app/slice';
import {
  FocusModeUtilitiesIconButton,
  UtilitiesIcon,
  UtilitiesIconButton,
} from '../styles';
import { useChromeConfigContext } from 'src/providers/ChromeConfigContextProvider';
import { shouldLoadSystemJsAssets } from 'src/commons/utils';
import { useDynamicIntroWidgetsContext } from 'src/providers/DynamicIntroWidgetsContextProvider';

/* eslint-disable-next-line */
declare var System: any;

interface WhatsNewStandaloneIconProps {
  fitUnreadFeatures: number;
  fitTotalFeatures: number;
}

const NotificationIcon = styled.div`
  display: inline-flex;
  justify-content: center;
  border-radius: 50%;
  top: 0;
  position: absolute;
  height: 20px;
  width: 20px;
  background: #d82624;
  right: -12px;
`;

const NotificationsText = styled(Text)`
  position: absolute;
  top: 0;
`;

const WhatsNewStandaloneIcon: React.VFC<WhatsNewStandaloneIconProps> = ({
  fitUnreadFeatures,
  fitTotalFeatures,
}) => {
  const hasLoadFitFuncRun = useRef(false);
  const chromeConfig = useChromeConfigContext();
  const { loadFitFunction } = useDynamicIntroWidgetsContext();
  const conditionallyLoadFitFunction = () => {
    // only need to run loadFitFunc once
    if (!hasLoadFitFuncRun.current) {
      // useRef instead of useState to prevent unnecessary rerenders
      loadFitFunction();
      hasLoadFitFuncRun.current = true;
    }
  };
  useEffect(() => {
    // if we don't load this immediately notification counts don't show up until click / focus
    conditionallyLoadFitFunction();
  }, [loadFitFunction, conditionallyLoadFitFunction]);

  const StyledIconButton =
    chromeConfig.pageLayoutType === PAGE_LAYOUT_TYPE.FOCUS
      ? FocusModeUtilitiesIconButton
      : UtilitiesIconButton;

  if (!fitTotalFeatures) {
    return null;
  }

  return (
    <StyledIconButton
      isNotificationButton
      data-testid="whats-new-standalone-icon"
      data-takt-id="aac-help-feature-center"
      onMouseEnter={conditionallyLoadFitFunction}
      onFocus={conditionallyLoadFitFunction}
      onClick={() => {
        conditionallyLoadFitFunction();
        if (shouldLoadSystemJsAssets()) {
          window.System.import('@amzn/intro-widgets-api').then((module) => {
            module.default.openNewFeatureCenter();
          });
        } else {
          import('@amzn/intro-widgets-api').then((module) =>
            module.openNewFeatureCenter(),
          );
        }
      }}
    >
      <UtilitiesIcon type={rocket} />
      {fitUnreadFeatures > 0 && (
        <NotificationIcon>
          <NotificationsText>{fitUnreadFeatures}</NotificationsText>
        </NotificationIcon>
      )}
    </StyledIconButton>
  );
};

const mapStateToProps = (state: RootState) => {
  return {
    fitUnreadFeatures: selectFitUnreadFeatures(state),
    fitTotalFeatures: selectFitTotalFeatures(state),
  };
};

export default connect(mapStateToProps)(WhatsNewStandaloneIcon);
